import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Button, Modal } from "@mui/material";
import { Typography } from "@material-ui/core";
import { PrimaryColor } from "../../../shared/styles/colors";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import Bundle from "../../../model/bundle";
import BundleModal from "./BundleModal";
import BundleItem from "../../../model/bundleItem";
import ApiService from "../../../service/ApiService";
import DatabaseEntities from "../../../model/enum/DatabaseEntities";
import useStyles from "./styles";
import UltraBundleModal from "./UltraBundleModal";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "35rem",
  bgcolor: "background.paper",
  order: "none",
  borderRadius: "8px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  maxHeight: "90vh", // Restrict maximum height to 80% of the viewport height
  overflowY: "auto", // Enable vertical scrolling
  "@media (max-width: 640px)": {
    width: "80%",
  },
};

interface Props {
  bundle: Bundle;
  onOpen?: (id?: string) => void;
  onClose?: () => void;
}

const BundleDetailModal = ({ bundle, onOpen, onClose }: Props) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  const handleOpen = () => {
    setOpen(true);
    if (onOpen) {
      onOpen(bundle.id?.toString());
    }
  };

  const handleClose = () => {
    setOpen(false);
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    const bundleId = searchParams.get("bundleId");
    if (!open && bundleId && bundleId === bundle.id?.toString()) {
      handleOpen();
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      await ApiService.get<Array<BundleItem>>(
        DatabaseEntities.ULTRA_BUNDLES + `/${bundle.id}`
      ).then((data) => {
        if (!data) return;

        bundle.BundleItems = data;
      });
      setLoading(false);
    };

    if (loading && bundle.isUltra) fetchData();
    else setLoading(false);
  });

  return (
    <div>
      <Button onClick={handleOpen}>
        <MoreVertIcon sx={{ color: PrimaryColor }} />
      </Button>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{ overflowY: "auto" }}
      >
        {loading ? (
          <Box sx={style}>
            <Typography className={classes.title}>Loading...</Typography>
          </Box>
        ) : (
          <Box sx={style}>
            <div className={classes.headerDiv}>
              <Typography className={classes.title}>Bundle details</Typography>
              <div className={classes.headerSubDiv}>
                {bundle.isUltra ? (
                  <UltraBundleModal ultrabundle={bundle} />
                ) : (
                  <BundleModal bundle={bundle} />
                )}
                <Button onClick={handleClose} sx={{ color: "black" }}>
                  <CloseIcon />
                </Button>
              </div>
            </div>
            <hr />
            <div className={classes.form}>
              {bundle.imageUrl && (
                <img
                  className={classes.imageDetail}
                  src={bundle.imageUrl}
                  alt="product img"
                />
              )}
              <Typography className={classes.label}>Name</Typography>
              <Typography className={classes.inputDetail}>
                {bundle.name ? bundle.name : bundle.task}
              </Typography>
              <Typography className={classes.label}>Description</Typography>
              <textarea
                className={classes.inputDetail}
                value={bundle.description}
                disabled
              />
              <Typography className={classes.label}>Is Ultra</Typography>
              <Typography className={classes.inputDetail}>
                {bundle.isUltra ? "Yes" : "No"}
              </Typography>
              {bundle?.BundleItems && bundle?.BundleItems?.length > 0 && (
                <div className="table-container">
                  <table className="styled-table">
                    <thead>
                      {bundle.isUltra ? (
                        <td>Bundle/Product</td>
                      ) : (
                        <td>Product/Service</td>
                      )}
                      <td>Quantity</td>
                    </thead>
                    <tbody>
                      {bundle?.BundleItems?.map((item: BundleItem) => (
                        <tr key={item.id}>
                          <td>
                            <Typography className={classes.inputDetail}>
                              {bundle.isUltra && item.Bundle
                                ? item.Bundle.name
                                : item.Product && item.Product?.name}
                            </Typography>
                          </td>
                          <td>
                            <Typography className={classes.inputDetail}>
                              {item.quantity}
                            </Typography>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </Box>
        )}
      </Modal>
    </div>
  );
};

export default BundleDetailModal;
